import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import type { StreamEvent } from "../api/hooks";
import {
  useActiveRun,
  useStopRun,
} from "../api/hooks";
import {
  streamAssistant,
  useAssistantConversations,
  useAssistantHistory,
  useDeleteConversation,
} from "../api/hooks";
import type { AssistantAction, AssistantMessage, Profile } from "../api/types";
import { ConversationMenu } from "./ConversationMenu";
import { RunProgress } from "./RunProgress";
import { renderMarkdown } from "../lib/markdown";

const GREETING = "Was möchten Sie recherchieren oder ändern?";

function fmtTime(value: string | Date): string {
  return new Date(value).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
}


const TOOL_LABELS: Record<string, string> = {
  get_technology_map: "Landkarte gelesen",
  plan_report: "Präsentation geplant",
  get_report: "Entwurf gelesen",
  update_report: "Folien gestaltet",
  preview_report: "Folien geprüft",
  create_report: "PDF erstellt",
  search_findings: "Datenbank durchsucht",
  get_finding: "Eintrag gelesen",
  update_finding: "Eintrag geändert",
  remove_finding: "Eintrag entfernt",
  restore_finding: "Eintrag wiederhergestellt",
  refine_finding_via_web: "Per Websuche nachgeschärft",
  aggregate_findings: "Bestand ausgezählt",
  web_search: "Im Web gesucht",
  fetch_url: "Seite gelesen",
  create_finding_from_url: "Eintrag angelegt",
  start_research_run: "Suchlauf gestartet",
  get_run_status: "Laufstatus geprüft",
  list_sources: "Quellen geprüft",
  list_rejects: "Verworfene geprüft",
  get_search_assignment: "Auftrag gelesen",
  update_search_assignment: "Auftrag geschärft",
  list_history: "Änderungen gelesen",
  revert_history: "Änderungen zurückgesetzt",
  list_clusters: "Themenbereiche gelesen",
  cluster_members: "Einträge des Themenbereichs gelesen",
  create_cluster: "Themenbereich angelegt",
  assign_cluster: "Technologien zugeordnet",
  remove_cluster: "Themenbereich entfernt",
  recluster: "Technologien neu geordnet",
  check_duplicates: "Dubletten geprüft",
  search_research: "Recherchematerial durchsucht",
};

const RUNNING_LABELS: Record<string, string> = {
  get_technology_map: "Liest die Technologie-Landkarte",
  plan_report: "Plant die Präsentation",
  get_report: "Liest den Entwurf",
  update_report: "Gestaltet die Folien",
  preview_report: "Prüft Layout und Lesbarkeit",
  create_report: "Erstellt das PDF",
  search_findings: "Durchsucht die Datenbank",
  web_search: "Sucht im Web",
  fetch_url: "Liest eine Seite",
  refine_finding_via_web: "Schärft per Websuche nach",
  start_research_run: "Startet einen Suchlauf",
  get_finding: "Liest die Einträge und Belege",
  update_finding: "Aktualisiert einen Eintrag",
  remove_finding: "Archiviert einen Eintrag",
  restore_finding: "Stellt einen Eintrag wieder her",
  aggregate_findings: "Wertet den Bestand aus",
  create_finding_from_url: "Übernimmt einen neuen Eintrag",
  get_run_status: "Prüft den Recherchefortschritt",
  list_sources: "Prüft die Quellen",
  list_rejects: "Prüft aussortierte Treffer",
  get_search_assignment: "Liest den Rechercheauftrag",
  update_search_assignment: "Präzisiert den Rechercheauftrag",
  list_history: "Prüft die bisherigen Änderungen",
  revert_history: "Setzt Änderungen zurück",
  list_clusters: "Liest die Themenbereiche",
  cluster_members: "Liest die Einträge des Themenbereichs",
  create_cluster: "Legt einen Themenbereich an",
  assign_cluster: "Ordnet Technologien zu",
  remove_cluster: "Entfernt einen Themenbereich",
  recluster: "Ordnet die Technologien neu",
  check_duplicates: "Prüft mögliche Dubletten",
  search_research: "Sichtet das Recherchematerial",
};

function phaseLabel(event?: StreamEvent | null): string {
  return event?.phase === "tool_input"
    ? runningLabel(event.tool ?? "")
    : event?.phase === "thinking" ? "Prüft die nächsten Schritte"
    : event?.phase === "answer" ? "Formuliert die Antwort"
    : "Verarbeitet Auftrag und Ergebnisse";
}

function toolLabel(tool: string): string {
  return TOOL_LABELS[tool] ?? "Arbeitsschritt abgeschlossen";
}

function runningLabel(tool: string): string {
  return RUNNING_LABELS[tool] ?? "Bearbeitet den nächsten Schritt";
}

/** The part after the label: what the step actually returned. */
function actionDetail(action: AssistantAction): string | null {
  const summary = actionSummary(action);
  const label = toolLabel(action.tool);
  return summary.startsWith(label + " · ") ? summary.slice(label.length + 3) : null;
}

function actionSummary(action: AssistantAction): string {
  const label = toolLabel(action.tool);
  const result = (action.result ?? {}) as Record<string, unknown>;
  if (typeof result.gesamt === "number") return `${label} · ${result.gesamt} Treffer`;
  if (result.geaendert) {
    const fields = Object.keys(result.geaendert as Record<string, unknown>);
    return fields.length ? `${label} · ${fields.length} Angaben aktualisiert` : `${label} · nichts geändert`;
  }
  if (result.gefuellt) {
    const fields = Object.keys(result.gefuellt as Record<string, unknown>);
    return fields.length ? `${label} · ${fields.length} Angaben ergänzt` : `${label} · nichts Neues`;
  }
  if (typeof result.entfernt === "string") return `${label} · ${result.entfernt}`;
  if (typeof result.wiederhergestellt === "string") {
    return `${label} · ${result.wiederhergestellt}`;
  }
  if (Array.isArray(result.treffer)) return `${label} · ${result.treffer.length} Ergebnisse`;
  if (result.error) return `${label} · Fehler`;
  return label;
}

interface LiveStep {
  label: string;
  detail: string | null;
  done: boolean;
}

interface LiveState {
  status: string;
  text: string;
  steps: LiveStep[];
}

const EMPTY_LIVE: LiveState = { status: "Verbindung wird hergestellt", text: "", steps: [] };

/** Tools that change what the list and the map show. Anything else — a
 *  search, a page read, a lookup — leaves the inventory alone and does not
 *  need a refetch mid-turn. */
const WRITES_INVENTORY = new Set([
  "create_cluster",
  "assign_cluster",
  "remove_cluster",
  "recluster",
  "create_finding_from_url",
  "update_finding",
  "remove_finding",
  "restore_finding",
  "refine_finding_via_web",
  "check_duplicates",
]);

export function ChatPanel({ profile, embedded = false }: { profile: Profile; embedded?: boolean }) {
  // undefined = not resolved yet (the last thread is adopted once the list
  // arrives), null = deliberately a new thread, string = that thread. Folding
  // the first two into one value made "+ Neu" snap back to the last thread.
  const [conversationId, setConversationId] = useState<string | null | undefined>(undefined);
  const conversations = useAssistantConversations(profile.id);
  const removeConversation = useDeleteConversation(profile.id);
  const queryClient = useQueryClient();
  const [input, setInput] = useState("");
  const [live, setLive] = useState<LiveState>(EMPTY_LIVE);
  const [busy, setBusy] = useState(false);
  const history = useAssistantHistory(profile.id, conversationId ?? null, !busy);
  const [pending, setPending] = useState<{ question: string; at: Date } | null>(null);
  // Fixed when the thread opens: a greeting whose clock ticks would be odd.
  const [greetedAt] = useState(() => new Date());
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const messages = history.data ?? [];

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages.length, live.status, live.text, live.steps, pending]);

  useEffect(() => () => abortRef.current?.abort(), []);

  // First load: continue where the user left off.
  useEffect(() => {
    if (conversationId === undefined && conversations.data?.[0] && !busy && !input) {
      setConversationId(conversations.data[0].id);
    }
  }, [conversationId, conversations.data, busy, input]);

  const send = async () => {
    const question = input.trim();
    if (!question || busy) return;
    setInput("");
    setError(null);
    setBusy(true);
    setLive(EMPTY_LIVE);

    const controller = new AbortController();
    abortRef.current = controller;
    const appended: AssistantMessage[] = [];
    let thread = conversationId ?? null;
    // The question appears immediately — waiting for the round trip made the
    // user's own turn invisible for the whole answer.
    setPending({ question, at: new Date() });

    const onEvent = (event: StreamEvent) => {
      switch (event.type) {
        case "start":
          setLive((s) => ({ ...s, status: "Auftrag wird vorbereitet" }));
          if (event.user_message) appended.push(event.user_message);
          // A question without a thread opens one; keep working in it.
          if (event.conversation_id) thread = event.conversation_id;
          break;
        case "error":
          setError(event.detail ?? "Unbekannter Fehler.");
          break;
        case "status":
          setLive((s) => ({ ...s, status: phaseLabel(event) }));
          break;
        case "snapshot": {
          const snapshot = event.snapshot;
          if (!snapshot) break;
          const actions = snapshot.actions ?? [];
          const steps: LiveStep[] = actions.map((action) => ({
            label: toolLabel(action.tool), detail: actionDetail(action), done: true,
          }));
          if (snapshot.running_tool) steps.push({
            label: runningLabel(snapshot.running_tool),
            detail: null, done: false,
          });
          setLive({ status: phaseLabel(snapshot.status), text: snapshot.text ?? "", steps });
          if (actions.some((action) => WRITES_INVENTORY.has(action.tool))) {
            void queryClient.invalidateQueries({ queryKey: ["findings"] });
            void queryClient.invalidateQueries({ queryKey: ["clusters"] });
          }
          break;
        }
        case "thinking":
          // Deliberate assistant updates belong in the card; model thinking
          // summaries (often English) are not part of the presentation.
          break;
        case "text":
          setLive((s) => ({ ...s, text: s.text + (event.text ?? "") }));
          break;
        case "tool_start":
          setLive((s) => ({
            ...s,
            steps: [
              ...s.steps,
              {
                label: runningLabel(event.tool ?? ""),
                detail: null,
                done: false,
              },
            ],
          }));
          break;
        case "tool_end":
          // Whatever changed the inventory is on screen a second later: the
          // theme appears when it is created, the entries as they are added.
          if (event.action && WRITES_INVENTORY.has(event.action.tool)) {
            void queryClient.invalidateQueries({ queryKey: ["findings"] });
            void queryClient.invalidateQueries({ queryKey: ["clusters"] });
          }
          setLive((s) => {
            const steps = s.steps.slice();
            const open = steps.map((x) => x.done).lastIndexOf(false);
            if (open >= 0 && event.action) {
              steps[open] = {
                label: toolLabel(event.action.tool),
                detail: actionDetail(event.action),
                done: true,
              };
            }
            return { ...s, steps };
          });
          break;
        case "done":
          if (event.assistant_message) appended.push(event.assistant_message);
          break;
      }
    };

    try {
      await streamAssistant(profile.id, question, thread, onEvent, controller.signal);
      queryClient.setQueryData<AssistantMessage[]>(
        ["assistant", profile.id, thread],
        (old) => [...(old ?? []), ...appended],
      );
      void queryClient.invalidateQueries({ queryKey: ["findings"] });
      // The assistant can redraw the map — new theme, moved entries.
      void queryClient.invalidateQueries({ queryKey: ["clusters"] });
      void queryClient.invalidateQueries({ queryKey: ["runs", profile.slug] });
    } catch (exception) {
      if ((exception as Error).name !== "AbortError") {
        setError((exception as Error).message);
      }
    } finally {
      setBusy(false);
      setLive(EMPTY_LIVE);
      setPending(null);
      abortRef.current = null;
      // Whatever happened — finished, aborted, crashed mid-stream — the
      // thread on the server is the truth; reload it.
      setConversationId(thread);
      void queryClient.invalidateQueries({ queryKey: ["assistant", profile.id, thread] });
      void queryClient.invalidateQueries({ queryKey: ["assistant-conversations", profile.id] });
    }
  };

  const stop = () => abortRef.current?.abort();

  // A search runs for minutes in the worker; the header is where one
  // notices it, and where one can call it off.
  const activeRun = useActiveRun(profile.slug);
  const stopRun = useStopRun(profile.slug);

  const threads = conversations.data ?? [];
  // Nothing selected yet and threads exist: the history endpoint returned the
  // most recent one, so the picker should show it rather than "new".
  const shownId = conversationId ?? null;
  const updatedThread = threads.find(thread => thread.id !== shownId &&
    new Date(thread.last_message_at ?? thread.created_at) > greetedAt);

  const picker = (
    <ConversationMenu
      conversations={threads}
      currentId={shownId || null}
      disabled={busy}
      onSelect={setConversationId}
      onDelete={(id) => {
        removeConversation.mutate(id);
        // Deleting the open thread leaves the panel on a fresh one.
        if (id === shownId) setConversationId(null);
      }}
    />
  );

  return (
    <div className={embedded ? "chat-embedded" : "chat-page"}>
      {embedded ? (
        <div className="split-chat-head">
          <span className={"chat-dot" + (busy ? " busy" : "")} />
          Assistent
          {activeRun && (
            <RunProgress
              run={activeRun}
              onStop={() => stopRun.mutate(activeRun.id)}
              stopping={stopRun.isPending}
            />
          )}
          <span className="chat-head-spacer" />
          <span className="picker-label">Historie:</span>
          {picker}
        </div>
      ) : (
        <div className="view-head">
          <div>
            <h1>{profile.mode === "funding" ? "Förderungen" : "Technologie"} · Chat</h1>
          </div>
          <span className="picker-label">Historie:</span>
          {picker}
        </div>
      )}

      <div className={"split-chat " + (embedded ? "chat-embedded-panel" : "chat-page-panel")}>
        {updatedThread && (
          <div className="chat-update-notice" role="status">
            <span>Neue Nachricht im Profil-Chat</span>
            <button className="btn secondary" disabled={busy}
              onClick={() => setConversationId(updatedThread.id)}>
              Nachricht öffnen
            </button>
          </div>
        )}
        <div className="chat-scroll" ref={scrollRef}>
          {/* isLoading, nicht isPending: Eine abgeschaltete Abfrage steht in
              TanStack Query dauerhaft auf „pending". Ein frisches Konto hat
              keine Unterhaltung, die Abfrage laeuft also nie los — und die
              Ladezeile stuende fuer immer da, waehrend die Begruessung
              darunter nie erscheint. */}
          {history.isLoading && <div className="chat-hint">Verlauf wird geladen…</div>}
          {!history.isLoading && messages.length === 0 && (
            <div className="chat-msg assistant">
              <div className="chat-row">
                <div className="chat-bubble markdown">{GREETING}</div>
              </div>
              <div className="chat-time">{fmtTime(greetedAt)}</div>
            </div>
          )}

          {messages.map((message) => (
            <Bubble key={message.id} message={message} />
          ))}

          {pending && (
            <div className="chat-msg user">
              <div className="chat-row">
                <div className="chat-bubble">{pending.question}</div>
              </div>
              <div className="chat-time">{fmtTime(pending.at)}</div>
            </div>
          )}

          {busy && (
            <div className="chat-msg assistant chat-msg-progress">
              <ProgressCard live={live} />
            </div>
          )}

          {error && (
            <div className="chat-hint" style={{ color: "var(--status-error)" }}>
              {error}
            </div>
          )}
        </div>

        <div className="chat-input-row">
          <textarea
            rows={1}
            placeholder="Nachricht…"
            value={input}
            disabled={busy}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                void send();
              }
            }}
          />
          {busy ? (
            <button className="btn secondary" onClick={stop}>
              Stoppen
            </button>
          ) : (
            <button className="btn" onClick={() => void send()} disabled={!input.trim()}>
              Senden
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Bubble({ message }: { message: AssistantMessage }) {
  const steps = message.actions.map((action) => ({
    label: toolLabel(action.tool), detail: actionDetail(action), done: true,
  }));
  return (
    <div className={`chat-msg ${message.role}`}>
      <div className="chat-row">
        {message.role === "assistant" ? (
          <div
            className="chat-bubble markdown"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(message.content) }}
          />
        ) : (
          <div className="chat-bubble">{message.content}</div>
        )}
      </div>
      {steps.length > 0 && <ActivityDetails steps={steps} />}
      <div className="chat-time">{fmtTime(message.created_at)}</div>
    </div>
  );
}

/** One readable interim answer, with a quiet activity signal that remains
 *  visible between text updates. No simulated percentage or typing cursor. */
function ProgressCard({ live }: { live: LiveState }) {
  const running = live.steps.find((step) => !step.done);
  return (
    <section className="chat-progress" aria-label="Aktueller Zwischenstand">
      <div className="chat-progress-heading">
        <span className="chat-progress-label">Zwischenstand</span>
        <span className="chat-progress-badge">
          <span className="agent-activity" aria-hidden="true"><i /><i /><i /></span>
          In Arbeit
        </span>
      </div>
      {live.text && (
        <div
          className="chat-bubble markdown chat-progress-text"
          dangerouslySetInnerHTML={{ __html: renderMarkdown(live.text) }}
        />
      )}
      <div className="chat-progress-status" role="status" aria-atomic="true">
        <span className="chat-progress-status-text">{running?.label ?? live.status}</span>
      </div>
      {live.steps.length > 0 && <ActivityDetails steps={live.steps} />}
    </section>
  );
}

/** Keep the audit trail available without competing with the readable text. */
function ActivityDetails({ steps }: { steps: LiveStep[] }) {
  return (
    <details className="chat-activity-details">
      <summary>
        <span>Aktivitäten</span>
        <span className="chat-activity-count">{steps.length}</span>
        <svg className="chat-activity-chevron" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="m4 2 4 4-4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </summary>
      <div className="steps">
        {steps.map((step, index) => (
          <div className={`step ${step.done ? "done" : "running"}`} key={index}>
            <span className="step-mark" aria-hidden="true">{step.done ? "✓" : "·"}</span>
            <span className="step-label">{step.label}</span>
            {step.detail && <span className="step-detail">{step.detail}</span>}
          </div>
        ))}
      </div>
    </details>
  );
}
